import React, { useRef, useCallback, ReactNode, MouseEvent } from "react";
import { buildURI, toCSV } from "./core";

export interface CSVHeader {
  label: string;
  key: string;
}

export interface CSVLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  data: string | any[] | (() => string | any[]);
  headers?: string[] | CSVHeader[];
  separator?: string;
  filename?: string;
  uFEFF?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>, done?: (proceed: boolean) => void) => void | boolean;
  asyncOnClick?: boolean;
  enclosingCharacter?: string;
  children?: ReactNode;
}

export const CSVLink = React.forwardRef<HTMLAnchorElement, CSVLinkProps>((props, ref) => {
  const {
    data,
    headers,
    separator,
    filename,
    uFEFF,
    children,
    onClick,
    asyncOnClick,
    enclosingCharacter,
    ...rest
  } = props;

  const linkRef = ref || useRef<HTMLAnchorElement>(null);

  // Avoid variable shadowing by using props directly
  const buildURIHandler = useCallback(() => {
    return buildURI(data, uFEFF, headers, separator, enclosingCharacter);
  }, [data, uFEFF, headers, separator, enclosingCharacter]);

  const handleLegacy = useCallback((event: MouseEvent<HTMLAnchorElement>, isAsync = false) => {
    if (
      typeof window !== "undefined" &&
      typeof (window.navigator as any).msSaveOrOpenBlob === "function"
    ) {
      event.preventDefault();
      const csvData = isAsync && typeof data === "function" ? data() : data;
      const blob = new Blob([
        uFEFF ? "\uFEFF" : "",
        toCSV(csvData, headers, separator, enclosingCharacter)
      ]);
      if (typeof (window.navigator as any).msSaveBlob === "function") {
        (window.navigator as any).msSaveBlob(blob, filename);
      }
      return false;
    }
  }, [data, headers, separator, filename, enclosingCharacter, uFEFF]);

  const handleAsyncClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      const done = (proceed: boolean) => {
        if (proceed === false) {
          event.preventDefault();
          return;
        }
        handleLegacy(event, true);
      };
      if (onClick) onClick(event, done);
    },
    [onClick, handleLegacy]
  );

  const handleSyncClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      const stopEvent = onClick && onClick(event) === false;
      if (stopEvent) {
        event.preventDefault();
        return;
      }
      handleLegacy(event);
    },
    [onClick, handleLegacy]
  );

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (typeof onClick === "function") {
        return asyncOnClick ? handleAsyncClick(event) : handleSyncClick(event);
      }
      handleLegacy(event);
    },
    [onClick, asyncOnClick, handleAsyncClick, handleSyncClick, handleLegacy]
  );

  const isNodeEnvironment = typeof window === "undefined";
  const href = isNodeEnvironment ? "" : buildURIHandler();
  CSVLink.displayName = "CSVLink";

  return (
    <a
      download={filename}
      {...rest}
      ref={linkRef}
      target="_self"
      href={href}
      onClick={handleClick}
    >
      {children}
    </a>
  );
});